import type { TaxRegistrationStatusName, WorkLocationResponse } from '../types';

/**
 * Step 5 list filtering (Figma S02-S19). Three dimensions (Location Type, Payroll Status, Tax Registration Status) plus a search box. Values
 * inside ONE dimension combine with OR; the dimensions and the search combine with AND. "All" resets a dimension (it is shown as an "All" chip
 * and filters nothing); Clear All resets every chip and the search.
 */

export type LocationTypeFilter = 'Main' | 'Branch' | 'RemoteHub' | 'ClientSite' | 'Temporary';
export type PayrollStatusFilter = 'Active' | 'Inactive';

export interface FilterSelection<T extends string> {
  /** The explicit "All" choice (shown as a grey "All" chip). Filters nothing. */
  all: boolean;
  values: T[];
}

export interface LocationFilters {
  type: FilterSelection<LocationTypeFilter>;
  payroll: FilterSelection<PayrollStatusFilter>;
  tax: FilterSelection<TaxRegistrationStatusName>;
  search: string;
}

export const NO_SELECTION = { all: false, values: [] as never[] };

export function emptyFilters(): LocationFilters {
  return { type: { all: false, values: [] }, payroll: { all: false, values: [] }, tax: { all: false, values: [] }, search: '' };
}

export const LOCATION_TYPE_OPTIONS: Array<{ value: LocationTypeFilter; label: string; chip: string }> = [
  { value: 'Main', label: 'Main', chip: 'Main' },
  { value: 'Branch', label: 'Branches', chip: 'Branch' },
  { value: 'RemoteHub', label: 'Remote Hub', chip: 'Remote Hub' },
  { value: 'ClientSite', label: 'Client Site', chip: 'Client Site' },
  { value: 'Temporary', label: 'Temporary', chip: 'Temporary' },
];

export const PAYROLL_STATUS_OPTIONS: Array<{ value: PayrollStatusFilter; label: string }> = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

export const TAX_STATUS_OPTIONS: Array<{ value: TaxRegistrationStatusName; label: string }> = [
  { value: 'Registered', label: 'Registered' },
  { value: 'InProgress', label: 'In Progress' },
  { value: 'NotRegistered', label: 'Not Registered' },
  { value: 'NotRequired', label: 'Not Required' },
];

export function typeOf(location: WorkLocationResponse): LocationTypeFilter {
  if (location.isDefault) return 'Main';
  switch (location.locationType) {
    case 'RemoteHub': return 'RemoteHub';
    case 'ClientSite': return 'ClientSite';
    case 'Temporary': return 'Temporary';
    default: return 'Branch';
  }
}

/** Archived locations count as Inactive for payroll: both are unavailable for new assignments. */
export function payrollStatusOf(location: WorkLocationResponse): PayrollStatusFilter {
  return location.status === 'Active' ? 'Active' : 'Inactive';
}

export function isDimensionActive(selection: FilterSelection<string>): boolean {
  return selection.all || selection.values.length > 0;
}

export function hasActiveFilters(filters: LocationFilters): boolean {
  return isDimensionActive(filters.type) || isDimensionActive(filters.payroll) || isDimensionActive(filters.tax) || filters.search.trim() !== '';
}

/** Choosing "All" replaces the dimension's selection with the explicit All state; choosing it again clears the dimension. */
export function toggleAll<T extends string>(selection: FilterSelection<T>): FilterSelection<T> {
  return selection.all ? { all: false, values: [] } : { all: true, values: [] };
}

/** Choosing a value leaves the "All" state; unchecking the last value returns the dimension to untouched. */
export function toggleValue<T extends string>(selection: FilterSelection<T>, value: T): FilterSelection<T> {
  const values = selection.values.includes(value) ? selection.values.filter((v) => v !== value) : [...selection.values, value];
  return { all: false, values };
}

function matchesDimension<T extends string>(selection: FilterSelection<T>, value: T): boolean {
  if (selection.all || selection.values.length === 0) return true;
  return selection.values.includes(value);
}

export function matchesSearch(location: WorkLocationResponse, search: string): boolean {
  const query = search.trim().toLowerCase();
  if (query === '') return true;
  return [location.name, location.city, location.state, location.zip, location.locationCode].some((field) => field.toLowerCase().includes(query));
}

export function applyFilters(locations: WorkLocationResponse[], filters: LocationFilters): WorkLocationResponse[] {
  return locations.filter(
    (l) =>
      matchesDimension(filters.type, typeOf(l))
      && matchesDimension(filters.payroll, payrollStatusOf(l))
      && matchesDimension(filters.tax, l.taxRegistrationStatus)
      && matchesSearch(l, filters.search),
  );
}
